import { pushToDataLayer } from '../../../common/analytics';
import { ESIASENNUSPALVELU } from '../../../common/constants/subscriptionConstants';
import { MobileM2MAddonCode } from '../../../common/enums';
import { postEppRedeemTerminate } from '../../../common/fetch';
import { eInvoiceMsg, emailInvoiceMsg, paperInvoiceMsg, t } from '../../../common/i18n';
import { formatAddress } from '../../../common/utils/accountUtils';
import { formatPhoneNumber } from '../../../common/utils/phoneNumberUtils';
import { isPOBoxAddress } from '../../../common/utils/validationUtils';
import { BillingAccountDeliveryMethod } from '../../../generated/api/billingAccountDeliveryMethod';
import {
  CommercialProductSubType,
  EnrollmentProgramType,
  EppRedeemTerminateRequestType,
  OnlineModelCategory,
  SapProductCategory,
} from '../../../generated/api/models';
import { createAvailability } from '../../ProductDetails/utils/formatters';
import { getAddonsForOfferItem } from '../../ShoppingBasket/shoppingBasketFunctions';
import { isMobileM2MProduct } from '../../ShoppingBasket/shoppingBasketHelpers';
import {
  calculateAddonPricesMonthly,
  calculateAddonPricesOneTime,
  calculateMonthlyAndOneTimeChargesTotals,
  getAddonPriceDisplayData,
  getCommercialProductForGuid,
  getEffectivePrice,
  getEntityPriceForGuid,
  getOfferForBasketItem,
  getOnlineModelForBasketItem,
  isSalesProductModel,
  isSelfServiceModel,
} from '../../ShoppingBasket/shoppingBasketUtils';
import { DeliveryModeType, getDeliveryMethods } from '../placeOrderTypes';
import { createAnalyticsItemsFromOffer, getEcommerceItemsForPlaceOrder } from './placeOrderAnalyticsUtils';
import type { EcommerceEventTypeV4 } from '../../../common/analytics';
import type { DeviceChangeOrRedeemRequest } from '../../../common/types/device';
import type { BasketItem, OfferItemAddon, ShoppingBasketType } from '../../../common/types/shoppingBasket';
import type { BillingAccount } from '../../../generated/api/billingAccount';
import type { ChildOrderItem } from '../../../generated/api/childOrderItem';
import type { ContactPerson } from '../../../generated/api/contactPerson';
import type { DeliveryAddress } from '../../../generated/api/deliveryAddress';
import type { DeliveryMethodsResponse } from '../../../generated/api/deliveryMethodsResponse';
import type { DeliveryType } from '../../../generated/api/deliveryType';
import type { AddOn, CommercialProduct, Offer } from '../../../generated/api/models';
import type { OnlineModel } from '../../../generated/api/onlineModel';
import type { DeliveryMethodEntry, PlaceOrderFormData, PlaceOrderItemUnit } from '../placeOrderTypes';
import type { UseFormReturn } from 'react-hook-form';

const EPP_PRODUCT_TYPE = 'EPP';
const DIRECT_DELIVERY_PRODUCT_TYPE = 'DIRECTDELIVERY';
const VAK_PRODUCT_TYPE = 'VAK';
const PRODUCT_DELIVERY_TYPE = 'ProductDeliveryType';

const ENROLLMENT_PROPERTY_NAME = 'Enrollment Program Type';

// Normalize by uppercasing and removing dashes, to match SFDC values. E.g. "Zero-Touch" -> "ZEROTOUCH"
const normalizeEnrollmentProgramType = (value: string): string => value.toUpperCase().replace(/-/g, '');

/**
 * Get the enrollment program type from an offer.
 * First checks offer.enrollmentProgramType, then falls back to offer.properties.
 * The fallback exists because some offers (computers with autopilot/zero-touch) may be missing
 * enrollmentProgramType but have it in properties. See OFI-60321.
 */
export const getEnrollmentProgramTypeFromOffer = (offer: Offer): EnrollmentProgramType | undefined => {
  if (offer.enrollmentProgramType) {
    return offer.enrollmentProgramType;
  }

  if (Array.isArray(offer.properties)) {
    const prop = offer.properties.find(
      p => p.name === ENROLLMENT_PROPERTY_NAME && p.value !== undefined && p.value !== null
    );
    if (prop) {
      const normalized = normalizeEnrollmentProgramType(prop.value as string);
      // If the normalized value matches an enum member, return it as EnrollmentProgramType
      const enumValues = Object.values(EnrollmentProgramType) as string[];
      if (enumValues.includes(normalized)) {
        return normalized as EnrollmentProgramType;
      }
      return EnrollmentProgramType.UNKNOWN;
    }
  }

  return undefined;
};

export interface AddonDisplayData {
  key: string;
  name: string;
  quantity: number;
  price: { amount: string; unit: string };
}

/**
 * Extract and format addon data for a basket item from placeOrderItemUnits.
 * Filters out bundled addons and M2M variant addons.
 */
export const getAddonsForBasketItem = (
  basketItemId: string,
  onlineModel: OnlineModel,
  basketItem: BasketItem,
  placeOrderItemUnits: PlaceOrderItemUnit[]
): AddonDisplayData[] => {
  const m2mVariantCodes = Object.values(MobileM2MAddonCode);
  const isMobileM2M = isMobileM2MProduct(onlineModel);

  const addOnsFromForm = placeOrderItemUnits
    .filter(u => u.basketItemId === basketItemId)
    .flatMap(unit => unit.addons)
    .map(addon => addon.addOnCode);

  const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModel);

  const uniqueModelAddOns = onlineModel.addOns?.filter(
    (addon, index, self) => self.findIndex(a => a.addOnCode === addon.addOnCode) === index
  );

  return (
    uniqueModelAddOns?.flatMap(modelAddOn => {
      const isInForm = addOnsFromForm.includes(modelAddOn.addOnCode);
      // Hide all isOfferAddOn=true addons from summary — they're auto-included for Salesforce
      // but should not show in the UI. Same filtering as basket display.
      const isOfferAddon = commercialProduct?.addOnAssociations?.some(
        a => a.addOnCode === modelAddOn.addOnCode && a.isOfferAddOn
      );
      const isM2MVariant = isMobileM2M && m2mVariantCodes.includes(modelAddOn.addOnCode as MobileM2MAddonCode);

      if (isInForm && !isOfferAddon && !isM2MVariant) {
        const addOnQuantity = addOnsFromForm.filter(addOnCode => addOnCode === modelAddOn.addOnCode).length;
        const addonPrice = getAddonPriceDisplayData(modelAddOn, addOnQuantity, commercialProduct, onlineModel);
        return [
          {
            key: `${basketItemId}_addon_${modelAddOn.addOnCode}`,
            name: modelAddOn.addOnProductName ?? '',
            quantity: addOnQuantity,
            price: addonPrice,
          },
        ];
      }
      return [];
    }) ?? []
  );
};

export const getContactInfoLines = (contactInfo?: ContactPerson): string[] => {
  if (!contactInfo) {
    return [];
  }
  const { firstName, lastName, phoneNumber, email } = contactInfo;

  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  const phone = phoneNumber ? formatPhoneNumber(phoneNumber) : '';

  return [name, phone, email].filter((v): v is string => v.trim().length > 0);
};

export const getDeliveryFields = (deliveryAddress: DeliveryAddress): string[] => {
  return [
    deliveryAddress.companyName,
    deliveryAddress.recipient,
    deliveryAddress.address.line1,
    deliveryAddress.address.line2,
    deliveryAddress.address.postalCode,
    deliveryAddress.address.postOffice,
  ].filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
};

export const isPOBoxDelivery = (
  postalCode?: string,
  deliveryMode: DeliveryModeType = DeliveryModeType.ADDRESS
): boolean => deliveryMode === DeliveryModeType.ADDRESS && Boolean(isPOBoxAddress(postalCode));

export const getBillingAccountDeliveryType = (billingAccount: BillingAccount): string => {
  switch (billingAccount.deliveryMethod) {
    case BillingAccountDeliveryMethod.EMAIL:
      return t.NNV9(emailInvoiceMsg);
    case BillingAccountDeliveryMethod.PAPER:
      return t.TER4(paperInvoiceMsg);
    case BillingAccountDeliveryMethod.ELECTRONIC:
      return t.IK1D(eInvoiceMsg);
    default:
      return '';
  }
};

export const getBillingAccountNameForDeliveryType = (billingAccount: BillingAccount): string => {
  switch (billingAccount.deliveryMethod) {
    case BillingAccountDeliveryMethod.EMAIL:
      return billingAccount.billReceiverName || '';
    case BillingAccountDeliveryMethod.PAPER:
      return formatAddress(billingAccount.payerAddress) || '';
    case BillingAccountDeliveryMethod.ELECTRONIC:
      return billingAccount.billElectronicOperator + ' ' + billingAccount.billElectronicAddress;
    default:
      return '';
  }
};

const DIRECT_DELIVERY_CATEGORIES = [SapProductCategory.ZBA1, SapProductCategory.ZBA2];

export const hasDirectDeliveryItems = (shoppingBasket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  return (
    shoppingBasket.items?.some(item => {
      const offer = getOfferForBasketItem(onlineModels, item);
      return offer?.sapProductCategory && DIRECT_DELIVERY_CATEGORIES.includes(offer.sapProductCategory);
    }) || false
  );
};

/**
 * Build productType array for delivery methods API call.
 * Mirrors the old DeviceCheckout's getProductDeliveryTypesInCart logic:
 * - Collects ProductDeliveryType properties from offers
 * - Adds 'EPP' if any basket item has an active EPP_DEVICE commercial product
 * - Adds 'DIRECTDELIVERY' if any item has a direct-delivery SAP category
 */
export const getProductDeliveryTypes = (basketItems: BasketItem[], onlineModels: OnlineModel[]): string[] => {
  const types: string[] = [];

  for (const item of basketItems) {
    const offer = getOfferForBasketItem(onlineModels, item);
    const deliveryType = offer?.properties?.find(p => p.name === PRODUCT_DELIVERY_TYPE)?.value;
    if (deliveryType) {
      types.push(deliveryType);
    }
  }

  const hasEppDevice = basketItems.some(item => {
    const model = getOnlineModelForBasketItem(onlineModels, item);
    const cp = getCommercialProductForGuid(item.offer.commercialProductGuid, model);
    return cp?.active && cp?.productSubType === CommercialProductSubType.EPP_DEVICE;
  });
  if (hasEppDevice) {
    types.push(EPP_PRODUCT_TYPE);
  }

  if (hasDirectDeliveryItems({ items: basketItems } as ShoppingBasketType, onlineModels)) {
    types.push(DIRECT_DELIVERY_PRODUCT_TYPE);
  }

  return [...new Set(types)];
};

export const hasBackorderedItems = (shoppingBasket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  return (
    shoppingBasket.items?.some(item => {
      const offer = getOfferForBasketItem(onlineModels, item);
      return offer && createAvailability(offer)?.isBackordered;
    }) || false
  );
};

export const hasPreInstallation = (shoppingBasket: ShoppingBasketType): boolean => {
  return shoppingBasket.items?.some(item => item.offer?.commercialProductGuid === ESIASENNUSPALVELU) || false;
};

/**
 * Check if basket has items that are out of stock but will be available later.
 * This includes backordered items and items with stockAvailability = 0 but SHIPMENTS_ARRIVING status.
 */
export const hasItemsOutOfStockButComingLater = (
  shoppingBasket: ShoppingBasketType,
  onlineModels: OnlineModel[]
): boolean => {
  return (
    shoppingBasket.items?.some(item => {
      const offer = getOfferForBasketItem(onlineModels, item);
      if (!offer) {
        return false;
      }

      // Check if explicitly out of stock but shipments are arriving
      if (offer.stockAvailability === 0 && offer.stockAvailabilityStatus === 'SHIPMENTS_ARRIVING') {
        return true;
      }

      // Check using existing availability logic (isBackordered)
      return createAvailability(offer)?.isBackordered || false;
    }) || false
  );
};

/**
 * Check if basket has items that are currently in stock and can ship immediately.
 */
export const hasItemsInStock = (shoppingBasket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  return (
    shoppingBasket.items?.some(item => {
      const offer = getOfferForBasketItem(onlineModels, item);
      if (!offer) {
        return false;
      }

      // If stockAvailability > 0 and not backordered, it's in stock
      if (offer.stockAvailability && offer.stockAvailability > 0) {
        return !createAvailability(offer)?.isBackordered;
      }

      return false;
    }) || false
  );
};

/**
 * Check if basket has mixed availability - some items in stock, some out of stock but coming later.
 */
export const hasMixedAvailability = (shoppingBasket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  return (
    hasItemsInStock(shoppingBasket, onlineModels) && hasItemsOutOfStockButComingLater(shoppingBasket, onlineModels)
  );
};

export const shouldShowShipmentType = (shoppingBasket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  if ((shoppingBasket.items?.length || 0) <= 1) {
    return false;
  }
  return (
    hasBackorderedItems(shoppingBasket, onlineModels) ||
    hasDirectDeliveryItems(shoppingBasket, onlineModels) ||
    hasMixedAvailability(shoppingBasket, onlineModels)
  );
};

export const hasVakProductItems = (shoppingBasket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  return (
    shoppingBasket.items?.some(item => {
      const offer = getOfferForBasketItem(onlineModels, item);
      return offer?.properties?.some(prop => prop.name === PRODUCT_DELIVERY_TYPE && prop.value === VAK_PRODUCT_TYPE);
    }) || false
  );
};

export const hasOnlySubscriptionItems = (shoppingBasket: ShoppingBasketType, onlineModels: OnlineModel[]): boolean => {
  if (!shoppingBasket.items?.length) {
    return false;
  }
  return shoppingBasket.items.every(item => {
    const model = onlineModels.find(m => m.onlineModelCode === item.guid);
    return (
      model?.category === OnlineModelCategory.SALES_PRODUCT || model?.category === OnlineModelCategory.SELF_SERVICE
    );
  });
};

export const isYttProduct = (model: OnlineModel | undefined): boolean => {
  return !!(
    model?.category === OnlineModelCategory.SALES_PRODUCT && model?.onlineModelName?.includes('Yritystietoturva')
  );
};

const isEsimSubscriptionItem = (model: OnlineModel | undefined, unit?: PlaceOrderItemUnit): boolean => {
  const isSubscription =
    model?.category === OnlineModelCategory.SALES_PRODUCT || model?.category === OnlineModelCategory.SELF_SERVICE;
  return isSubscription && unit?.simType === 'ESIM';
};

const isExistingSimActivationItem = (model: OnlineModel | undefined, unit?: PlaceOrderItemUnit): boolean => {
  const isSubscription =
    model?.category === OnlineModelCategory.SALES_PRODUCT || model?.category === OnlineModelCategory.SELF_SERVICE;
  return !!(isSubscription && unit?.simCardNumber);
};

export const hasNothingToDeliver = (
  shoppingBasket: ShoppingBasketType,
  onlineModels: OnlineModel[],
  formData: PlaceOrderFormData
): boolean => {
  if (!shoppingBasket.items?.length) {
    return false;
  }

  const units = formData.products?.placeOrderItemUnits || [];

  return shoppingBasket.items.every(item => {
    const model = getOnlineModelForBasketItem(onlineModels, item);

    // Digital sales products (e.g. Yritystietoturva) never need delivery regardless of units
    if (isYttProduct(model)) {
      return true;
    }

    // Find all units belonging to this basket item (quantity > 1 produces multiple units)
    const itemUnits = units.filter(u => u.basketItemId === item.id);

    // If no units exist yet for this item, we can't determine delivery — assume delivery needed
    if (itemUnits.length === 0) {
      return false;
    }

    // Every unit for this basket item must be eSIM or existing SIM activation
    return itemUnits.every(unit => isEsimSubscriptionItem(model, unit) || isExistingSimActivationItem(model, unit));
  });
};

export const getDeliveryMethodEntry = (deliveryTypeCode: DeliveryType): DeliveryMethodEntry | undefined => {
  return getDeliveryMethods().find(method => method.deliveryTypeCode === deliveryTypeCode);
};

export type MergedDeliveryMethod = DeliveryMethodEntry & {
  price: number;
  isActive: boolean;
};

export const mergeDeliveryMethods = (apiResponse: DeliveryMethodsResponse): MergedDeliveryMethod[] => {
  return getDeliveryMethods().flatMap(method => {
    const apiMethod = apiResponse.deliveryMethods.find(m => m.deliveryTypeCode === method.deliveryTypeCode);
    if (!apiMethod) {
      return [];
    }
    return [{ ...method, price: apiMethod.price, isActive: apiMethod.isActive }];
  });
};

export const filterAvailableMethods = (
  methods: MergedDeliveryMethod[],
  allowedModes: DeliveryModeType[]
): MergedDeliveryMethod[] => {
  return methods.filter(({ mode, isActive }) => isActive && allowedModes.includes(mode));
};

export const requireValue = <T>(value: T | null | undefined, message: string): T => {
  if (value == null) {
    throw new Error(message);
  }
  return value;
};

export const getAddOnAssociationCode = (cp: CommercialProduct, code: string) => {
  return cp.addOnAssociations?.find(a => a.addOnCode === code);
};

export const getAddons = (cp: CommercialProduct, addOns: OfferItemAddon): ChildOrderItem[] => {
  if (!addOns) {
    return [];
  }
  return (
    addOns.addOnCodes?.map(code => {
      const association = getAddOnAssociationCode(cp, code);
      const addOnAssociationCode = association?.addOnAssociationCode;

      return {
        addOnCode: code,
        addOnAssociationCode: addOnAssociationCode ?? '',
      };
    }) || []
  );
};

export const resetMainFormOrderItemUnits = (
  methods: UseFormReturn<PlaceOrderFormData>,
  orderItemUnits: PlaceOrderItemUnit[]
) => {
  const mainFormValues = methods.getValues();

  methods.reset(
    {
      ...mainFormValues,
      products: {
        placeOrderItemUnits: orderItemUnits,
      },
    },
    { keepDefaultValues: true }
  );
};

export const handleEppRedeem = async (deviceChange: DeviceChangeOrRedeemRequest): Promise<void> => {
  const { redeemRequestType, replacedSubscriptionId, replacedSubscriptionBillingAccountId } = deviceChange;

  if (!redeemRequestType || !replacedSubscriptionId) {
    throw new Error('Redeem request type/Replaced subscription ID is missing');
  }

  await postEppRedeemTerminate(replacedSubscriptionId, {
    requestType: redeemRequestType,
    companyRedeem:
      redeemRequestType === EppRedeemTerminateRequestType.COMPANY_REDEEM && replacedSubscriptionBillingAccountId
        ? { billingAccountId: replacedSubscriptionBillingAccountId }
        : undefined,
  });
};

export interface OrderTotals {
  /** One-time charges for non-subscription items (devices, accessories) */
  totalOneTimeCharge: number;
  /** Monthly recurring charges for non-subscription items */
  totalMonthlyRecurringCharge: number;
  /** Fixed term total (monthly × payments) for non-subscription items only */
  totalFixedTermCharge: number;
  /** Monthly recurring charges for subscription items (SELF_SERVICE) */
  totalSubscriptionMonthlyRecurringCharge: number;
  /** One-time charges for subscription items (SELF_SERVICE) */
  totalSubscriptionOneTimeCharge: number;
  /** Monthly recurring charges for sales products (SALES_PRODUCT) */
  totalSalesProductsMonthlyRecurringCharge: number;
  /** One-time charges for sales products (SALES_PRODUCT) */
  totalSalesProductsOneTimeCharge: number;
}

const getAllAddons = (
  addOns: string[],
  model: OnlineModel,
  basketItem: BasketItem
): { modelAddOn: AddOn; addonCount: number }[] => {
  const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, model);

  return (
    model.addOns?.flatMap(modelAddOn => {
      // isOfferAddOn add-ons are auto-included and their price is already baked into the commercial
      // product's effective price — by site-backend for the public API, and by the v3 private price
      // service (calculateEntityPriceFromRequiredAddOns) for logged-in users. Counting them again here
      // would double the subscription totals and fail backend order validation, so skip them. This
      // mirrors getDisplayableAddons, which already excludes isOfferAddOn add-ons from the basket totals.
      const isOfferAddon = commercialProduct?.addOnAssociations?.some(
        a => a.addOnCode === modelAddOn.addOnCode && a.isOfferAddOn
      );

      const addonCount =
        basketItem.offer.addOns?.reduce(
          (count, offerItemAddon) => count + (offerItemAddon.addOnCodes?.includes(modelAddOn.addOnCode) ? 1 : 0),
          0
        ) ?? 0;

      if (addOns.includes(modelAddOn.addOnCode) && !isOfferAddon) {
        return [
          {
            modelAddOn,
            addonCount,
          },
        ];
      }
      return [];
    }) || []
  );
};

/**
 * Calculate order totals for the private place-order payload.
 * Item types:
 * - devices: everything that is not SELF_SERVICE or SALES_PRODUCT
 * - subscriptions: SELF_SERVICE
 * - sales products: SALES_PRODUCT
 *
 * Totals are reported separately per type where needed.
 * Fixed term charges are calculated only for device items.
 *
 * This is the ONE place in the UI that prices subscriptions off the commercial product (CP) price
 * (getEntityPriceForGuid). Everywhere else (subscription cards, cart total, checkout summary) prices
 * from the offer add-on composition instead. This is done to match backend order validation, which
 * also prices off the CP price for subscriptions.
 *
 * For the mobile product family, the CP price is 0 in Salesforce and the price is composed entirely of
 * offer add-ons. For other product families, like devices, which are not composed of add-ons, the CP has
 * the full price natively in Salesforce.
 *
 * Where the CP price comes from per API:
 * - public model API: site-backend takes the CP charges from the Salesforce productConfiguration totals
 *   (Salesforce aggregates offer add-ons into those totals internally)
 * - v3 private model API: re-composes the CP price from isOfferAddOn add-ons to match public
 *   (Salesforce bulk prices: returns 0 for the CP (only add-on prices) — does NOT compose)
 *
 * Consequence: isOfferAddOn add-ons are already baked into the CP price here, so getAllAddons must NOT
 * re-add them (it would double subscription totals). See the note in getAllAddons.
 */
export const calculateOrderTotals = (shoppingBasket: ShoppingBasketType, onlineModels: OnlineModel[]): OrderTotals => {
  const items = shoppingBasket.items ?? [];

  const deviceItems = items.filter(item => {
    const model = getOnlineModelForBasketItem(onlineModels, item);
    return !isSelfServiceModel(model) && !isSalesProductModel(model);
  });

  const subscriptionItems = items.filter(item => {
    const model = getOnlineModelForBasketItem(onlineModels, item);
    return isSelfServiceModel(model);
  });

  const salesProductItems = items.filter(item => {
    const model = getOnlineModelForBasketItem(onlineModels, item);
    return isSalesProductModel(model);
  });

  const { monthlyRecurringCharges: devicesMonthlyRecurringCharges, onetimeCharges: devicesOneTimeCharges } =
    calculateMonthlyAndOneTimeChargesTotals(onlineModels, deviceItems);

  const { monthlyRecurringCharges: salesProductsMonthlyRecurringCharges, onetimeCharges: salesProductsOneTimeCharges } =
    calculateMonthlyAndOneTimeChargesTotals(onlineModels, salesProductItems);

  const subscriptionTotals = subscriptionItems?.reduce(
    (acc, basketItem) => {
      const addonsForOfferItem = getAddonsForOfferItem(basketItem.offer);
      const onlineModelForItem = getOnlineModelForBasketItem(onlineModels, basketItem);
      if (!onlineModelForItem) {
        return acc;
      }

      const priceFromCp = getEntityPriceForGuid(basketItem.offer.commercialProductGuid, onlineModelForItem);
      acc.monthlyRecurringCharges += (priceFromCp?.effectivePrice.monthlyRecurringCharge || 0) * basketItem.quantity;
      acc.onetimeCharges += (priceFromCp?.effectivePrice.oneTimeCharge || 0) * basketItem.quantity;

      const addons = getAllAddons(addonsForOfferItem, onlineModelForItem, basketItem);
      const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModelForItem);
      const addonOneTime = calculateAddonPricesOneTime(addons, commercialProduct);
      const addonMonthly = calculateAddonPricesMonthly(addons, commercialProduct, false);
      acc.onetimeCharges += addonOneTime;
      acc.monthlyRecurringCharges += addonMonthly;
      return acc;
    },
    { monthlyRecurringCharges: 0, onetimeCharges: 0 }
  );

  const fixedTermCharges = deviceItems.reduce((acc, basketItem) => {
    const model = getOnlineModelForBasketItem(onlineModels, basketItem);
    const priceData = getEffectivePrice(basketItem, model, onlineModels);
    const monthly = priceData?.price?.monthlyRecurringCharge ?? 0;
    const payments = priceData?.price?.payments ?? 0;
    if (monthly > 0 && payments > 0) {
      acc += monthly * payments * basketItem.quantity;
    }
    return acc;
  }, 0);

  return {
    totalOneTimeCharge: devicesOneTimeCharges,
    totalMonthlyRecurringCharge: devicesMonthlyRecurringCharges,
    totalFixedTermCharge: fixedTermCharges,
    totalSubscriptionMonthlyRecurringCharge: subscriptionTotals?.monthlyRecurringCharges,
    totalSubscriptionOneTimeCharge: subscriptionTotals?.onetimeCharges,
    totalSalesProductsMonthlyRecurringCharge: salesProductsMonthlyRecurringCharges,
    totalSalesProductsOneTimeCharge: salesProductsOneTimeCharges,
  };
};

/**
 * Handle checkout analytics for shopping basket - tracks BEGIN_CHECKOUT, ADD_SHIPPING_INFO, ADD_PAYMENT_INFO events
 * Converts basket items directly to EcommerceItem format and pushes to dataLayer
 */
export const handleBasketCheckoutAnalytics = (
  shoppingBasket: ShoppingBasketType,
  eventType: EcommerceEventTypeV4,
  onlineModels: OnlineModel[]
): void => {
  if (!shoppingBasket?.items?.length) {
    return;
  }

  const allEcommerceItems = [];

  for (const basketItem of shoppingBasket.items) {
    const model = getOnlineModelForBasketItem(onlineModels, basketItem);
    if (!model) {
      continue;
    }

    const offer = model.offers.find(o => o.offerCode === basketItem.offer.guid);
    if (!offer) {
      continue;
    }

    if (isSalesProductModel(model)) {
      const items = getEcommerceItemsForPlaceOrder(
        model.onlineModelName,
        offer,
        basketItem.quantity,
        eventType,
        model,
        []
      );
      allEcommerceItems.push(...items);
    } else {
      const items = createAnalyticsItemsFromOffer(
        offer,
        model,
        basketItem,
        basketItem.quantity,
        shoppingBasket.vouchers?.[0]
      );
      allEcommerceItems.push(...items);
    }
  }

  if (allEcommerceItems.length > 0) {
    const transactionCoupon = shoppingBasket.vouchers?.[0];

    pushToDataLayer({
      event: eventType,
      ecommerce: {
        ...(transactionCoupon ? { coupon: transactionCoupon } : {}),
        items: allEcommerceItems,
      },
    });
  }
};
